import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"
import { useContext, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { AgentMessageContainer } from "@/components/Chat/AgentMessageStyles.ts"
import { InviteHelpDialog } from "@/components/Chat/NewProject/InviteHelpDialog.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { TaskViewModelContext } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"

import type { AiChatMessageContent, TID } from "@/lib/client/generated/TypeDefinitions.ts"


export const MaxFixAttemptsExceededMessage = observer((props: {
  content: AiChatMessageContent.InitProject.MaxFixAttemptsExceeded
  isActive: boolean
}) => {
  const permissions = useOrganizationPermissions()
  const taskModel = useContext(TaskViewModelContext)

  const [inviteDeveloperDialogOpen, setInviteDeveloperDialogOpen] = useState(false)
  const [inviteSupportDialogOpen, setInviteSupportDialogOpen] = useState(false)

  return (
    <AgentMessageContainer isActive={props.isActive}>
      <Pill>
        <Content>
          <Title>
            Sorry, setup didn't succeed
          </Title>
          <p>
            {props.content.lastTryFailureSummary && props.content.lastTryFailureSummary.trim() !== ""
              ? ` ${props.content.lastTryFailureSummary.trim()} `
              : " "}
          </p>
          <span>
              Please invite your developer teammate or request our support team to help finish the setup.
          </span>
        </Content>

        {taskModel && (
          <Horizontal gap="16px" wrap="wrap">
            {permissions.canInviteUsers && (
              <Button
                kind="primary"
                size="large"
                onClick={() => setInviteDeveloperDialogOpen(true)}
                data-testid="invite-teammate-button"
              >
                Invite developer
              </Button>
            )}

            <RequestSupportButton
              kind="alternative"
              size="large"
              onClick={() => setInviteSupportDialogOpen(true)}
              data-testid="request-support-button"
            >
              Request support
            </RequestSupportButton>

            {permissions.canInviteUsers && (
              <InviteHelpDialog
                mode="developer"
                dialogOpen={inviteDeveloperDialogOpen}
                setDialogOpen={setInviteDeveloperDialogOpen}
                projectKey={taskModel.project.get().key}
                taskId={taskModel.taskId as TID}
              />
            )}

            <InviteHelpDialog
              mode="support"
              dialogOpen={inviteSupportDialogOpen}
              setDialogOpen={setInviteSupportDialogOpen}
              taskId={taskModel.taskId as TID}
            />
          </Horizontal>
        )}
      </Pill>
    </AgentMessageContainer>
  )
})

const Content = styled(Vertical)`
  gap: 4px;
  padding-bottom: 16px;
  font-weight: 400;
`

const Pill = styled(Vertical)`
  border-radius: var(--border-radius-20);

  padding: 12px 16px;
  background: rgb(26 26 28 / 5%);
`

const Title = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
`

const RequestSupportButton = styled(Button)`
  background: transparent;
  border-color: var(--text-color-10);
`
